class KaarreHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.querySelector('[data-site-header]');
    this.overlay = this.querySelector('[data-header-overlay]');
    this.section = this.closest('.shopify-section');
    this.navItems = [...this.querySelectorAll('[data-mega-item]')];
    this.desktopQuery = window.matchMedia('(min-width: 1280px)');
    this.stickyEnabled = this.dataset.sticky === 'true';
    this.abortController = new AbortController();
    this.activeItem = null;
    this.frameRequested = false;
    const { signal } = this.abortController;

    this.addEventListener('click', (event) => this.handleClick(event), { signal });
    document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    this.overlay?.addEventListener('click', () => this.closeMegaMenu(), { signal });
    this.desktopQuery.addEventListener('change', () => this.handleBreakpointChange(), { signal });

    this.navItems.forEach((item) => {
      item.addEventListener('pointerenter', (event) => this.handlePointerEnter(event, item), { signal });
      item.addEventListener('pointerleave', (event) => this.handlePointerLeave(event), { signal });
      item.addEventListener('focusin', (event) => this.handleFocusIn(event, item), { signal });
      item.addEventListener('focusout', (event) => this.handleFocusOut(event, item), { signal });
    });

    if (this.stickyEnabled && this.header && this.section) {
      window.addEventListener('scroll', () => this.requestStickyUpdate(), { passive: true, signal });
      window.addEventListener('resize', () => this.recalculateStickyPoint(), { passive: true, signal });
      requestAnimationFrame(() => this.recalculateStickyPoint());
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
  }

  handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const toggle = target?.closest('[data-mega-toggle]');

    if (!toggle || !this.contains(toggle) || !this.desktopQuery.matches) return;

    event.preventDefault();
    const item = toggle.closest('[data-mega-item]');

    if (item === this.activeItem) {
      this.closeMegaMenu({ restoreFocus: true });
    } else {
      this.openMegaMenu(item);
    }
  }

  handleKeydown(event) {
    if (event.key !== 'Escape' || !this.activeItem) return;

    event.preventDefault();
    this.closeMegaMenu({ restoreFocus: true });
  }

  handlePointerEnter(event, item) {
    if (!this.desktopQuery.matches || event.pointerType === 'touch') return;

    window.clearTimeout(this.closeTimer);
    this.openTimer = window.setTimeout(() => this.openMegaMenu(item), 120);
  }

  handlePointerLeave(event) {
    if (!this.desktopQuery.matches || event.pointerType === 'touch') return;

    window.clearTimeout(this.openTimer);
    this.closeTimer = window.setTimeout(() => this.closeMegaMenu(), 160);
  }

  handleFocusIn(event, item) {
    if (!this.desktopQuery.matches) return;

    window.clearTimeout(this.closeTimer);

    if (!event.target.closest('[data-mega-toggle]')) {
      this.openMegaMenu(item);
    }
  }

  handleFocusOut(event, item) {
    if (!this.desktopQuery.matches || item.contains(event.relatedTarget)) return;

    this.closeTimer = window.setTimeout(() => this.closeMegaMenu(), 160);
  }

  handleBreakpointChange() {
    if (!this.desktopQuery.matches) {
      this.closeMegaMenu();
    }

    this.recalculateStickyPoint();
  }

  openMegaMenu(item) {
    if (!item || !this.desktopQuery.matches) return;

    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);

    if (this.activeItem && this.activeItem !== item) {
      this.setItemState(this.activeItem, false);
    }

    this.activeItem = item;
    this.setItemState(item, true);
    this.overlay?.classList.add('is-visible');
    this.overlay?.setAttribute('aria-hidden', 'false');
  }

  closeMegaMenu({ restoreFocus = false } = {}) {
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);

    if (!this.activeItem) return;

    const toggle = this.activeItem.querySelector('[data-mega-toggle]');
    this.setItemState(this.activeItem, false);
    this.activeItem = null;
    this.overlay?.classList.remove('is-visible');
    this.overlay?.setAttribute('aria-hidden', 'true');

    if (restoreFocus) {
      toggle?.focus();
    }
  }

  setItemState(item, isOpen) {
    const toggle = item.querySelector('[data-mega-toggle]');
    const panel = item.querySelector('[data-mega-panel]');
    item.classList.toggle('is-open', isOpen);
    toggle?.setAttribute('aria-expanded', String(isOpen));
    panel?.setAttribute('aria-hidden', String(!isOpen));
  }

  recalculateStickyPoint() {
    if (!this.stickyEnabled || !this.section) return;

    this.stickyPoint = this.section.getBoundingClientRect().top + window.scrollY;
    this.updateStickyState();
  }

  requestStickyUpdate() {
    if (this.frameRequested) return;

    this.frameRequested = true;
    requestAnimationFrame(() => {
      this.updateStickyState();
      this.frameRequested = false;
    });
  }

  updateStickyState() {
    const isStuck = window.scrollY > this.stickyPoint;
    this.header?.classList.toggle('is-stuck', isStuck);
  }
}

class KaarreMobileNavigation extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.rootView = this.querySelector('[data-mobile-nav-root]');
    this.backButton = this.querySelector('[data-mobile-nav-back]');
    this.titleElement = this.querySelector('[data-mobile-nav-title]');
    this.viewport = this.querySelector('.mobile-nav__viewport');
    this.initialTitle = this.dataset.initialTitle || '';
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.addEventListener('click', (event) => this.handleClick(event), { signal });
    this.dialog?.addEventListener('close', () => this.resetNavigation(false), { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  handleClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const opener = target?.closest('[data-mobile-nav-open]');

    if (opener && this.contains(opener)) {
      event.preventDefault();
      this.openPanel(opener);
      return;
    }

    const back = target?.closest('[data-mobile-nav-back]');

    if (back && this.contains(back)) {
      event.preventDefault();
      this.resetNavigation(true);
    }
  }

  openPanel(opener) {
    const panelId = opener.dataset.mobileNavOpen;
    const panel = [...this.querySelectorAll('[data-mobile-nav-panel]')].find(
      (candidate) => candidate.id === panelId,
    );

    if (!panel) return;

    this.activeOpener?.setAttribute('aria-expanded', 'false');
    this.activePanel?.setAttribute('hidden', '');
    this.activeOpener = opener;
    this.activePanel = panel;
    this.rootView?.setAttribute('hidden', '');
    panel.removeAttribute('hidden');
    opener.setAttribute('aria-expanded', 'true');
    this.backButton?.removeAttribute('hidden');

    if (this.titleElement) {
      this.titleElement.textContent = opener.dataset.mobileNavHeading || this.initialTitle;
    }

    if (this.viewport) {
      this.viewport.scrollTop = 0;
    }

    requestAnimationFrame(() => this.backButton?.focus());
  }

  resetNavigation(restoreFocus) {
    const opener = this.activeOpener;
    this.activePanel?.setAttribute('hidden', '');
    this.activeOpener?.setAttribute('aria-expanded', 'false');
    this.rootView?.removeAttribute('hidden');
    this.backButton?.setAttribute('hidden', '');

    if (this.titleElement) {
      this.titleElement.textContent = this.initialTitle;
    }

    if (this.viewport) {
      this.viewport.scrollTop = 0;
    }

    this.activePanel = null;
    this.activeOpener = null;

    if (restoreFocus && opener?.isConnected) {
      opener.focus();
    }
  }
}

if (!customElements.get('kaarre-header')) {
  customElements.define('kaarre-header', KaarreHeader);
}

if (!customElements.get('kaarre-mobile-navigation')) {
  customElements.define('kaarre-mobile-navigation', KaarreMobileNavigation);
}
